
import LockRepoDialog from '@/components/dialogs/backups/LockRepoDialog.vue';
import {
  BackupRepoDto,
  BackupRepoRepositoryInterface,
  LockType,
  RepoLocksRepositoryInterface,
  RepoLocksDto,
} from '@/services/Backup/interfaces';
import { AxiosError } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import { BackupRepoId } from '@/services/Backup/interfaces/index';
import BackupRepoDetailsDialog from '@/components/dialogs/backups/BackupRepoDetailsDialog.vue';
import DeleteBackupRepoDialog from '@/components/dialogs/backups/DeleteBackupRepoDialog.vue';
import PruneBackupRepoDialog from '@/components/dialogs/backups/PruneBackupRepoDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  components: {
    DeleteBackupRepoDialog,
    PruneBackupRepoDialog,
    BackupRepoDetailsDialog,
    PageHeader,
  },
  inject: ['backupRepoRepository', 'repoLocksRepository'],
})
export default class AllBackupRepos extends Vue {
  public loading: boolean = false;
  public options: ListPaginationOptions = defaultListOptions();
  public totalItems: number = 0;
  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  public backupRepoRepository!: BackupRepoRepositoryInterface;
  public repoLocksRepository!: RepoLocksRepositoryInterface;
  public backupRepos: BackupRepoDto[] = [];

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Repository ID', value: 'repositoryId' },
    { text: 'Repository Path', value: 'repositoryPath' },
    { text: 'Default For Origin Server', value: 'targetOriginServerId' },
    { text: 'Backup Server', value: 'backupServerId' },
    { text: 'Actions', value: 'actions' },
  ];

  public deleteDialog: string = '';
  public deleteDialogData: BackupRepoDto | null = null;

  public viewDialog: string = '';
  public viewDialogData: BackupRepoDto | null = null;

  public selectedRepoLock: RepoLocksDto | null = null;

  public lockDialogVisible: boolean = false;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = buildListPaginationQuery(this.options);
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'backup_repositories', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    await this.search();
  }

  public async search(): Promise<void> {
    this.loading = true;
    try {
      await this.getBackupRepoRepositories();
    } catch (err) {
      await this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  public async getBackupRepoRepositories(): Promise<void> {
    const result = await this.backupRepoRepository.getAllBackupRepos({
      page: this.options.page,
      pageSize: effectivePageSize(this.options.itemsPerPage),
    });
    this.totalItems = result?.total ?? 0;
    this.backupRepos = result?.data ?? [];
  }

  public async onPageChange(): Promise<void> {
    await this.syncRouteQuery();
    await this.search();
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.search();
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'backup_repositories') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.applyRouteQuery();
    await this.search();
  }

  public async showDeleteDialogFor(id: BackupRepoId): Promise<void> {
    try {
      const backupRepo = await this.backupRepoRepository.getBackupRepo(id);
      if (!backupRepo) {
        return;
      }
      this.deleteDialog = 'delete-backup-repo-dialog';
      this.deleteDialogData = backupRepo;
    } catch (err) {
      this.onError(err);
    }
  }

  public async showViewDialogFor(id: BackupRepoId): Promise<void> {
    try {
      const backupRepo = this.$store.getters.isAdmin
          ? await this.backupRepoRepository.getBackupRepoSecrets(id)
          : await this.backupRepoRepository.getBackupRepo(id);

      if (!backupRepo) {
        return;
      }

      this.viewDialog = 'backup-repo-details-dialog';
      this.viewDialogData = backupRepo;
    } catch (err) {
      this.onError(err);
    }
  }

  public async createBackup(backupRepo?: BackupRepoDto): Promise<void> {
    this.$router.push({
      name: 'backup_create',
      query: { backupRepoId: backupRepo?.id.toString() },
    });
  }

  public async createBackupRepository(): Promise<void> {
    this.$router.push({
      name: 'backup_repository_create',
    });
  }

  public async pruneRepository(backupRepo?: BackupRepoDto): Promise<void> {
    this.$router.push({
      name: 'prune_create',
      query: { backupRepoId: backupRepo?.id.toString() },
    });
  }

  public async forgetRepository(backupRepo?: BackupRepoDto): Promise<void> {
    this.$router.push({
      name: 'forget_create',
      query: { backupRepoId: backupRepo?.id.toString() },
    });
  }

  public async viewLockRepos(backupRepo?: BackupRepoDto): Promise<void> {
    this.$router.push({
      name: 'backup_repo_locks',
      query: { backupRepoId: backupRepo?.id.toString() },
    });
  }

  public async deleteBackupRepo(backupRepo: BackupRepoDto): Promise<void> {
    try {
      await this.backupRepoRepository.deleteBackupRepo(backupRepo.id);
      this.$successDialog.show('Backup repo deleted successfully');
      await this.search();
    } catch (err) {
      this.onError(err);
    }
  }

  private async onError(err: unknown): Promise<void> {
    console.log(err);
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
